import configuration from './configuration'
import state from '../state/sidebar-state'

const REDACTED = '[REDACTED]'

// Header/field names whose values must never be logged. Axios attaches the outgoing
// request config (including these headers) to the errors it throws, so anything that
// serializes such an error would otherwise ship a live bearer token to the logging API.
const SENSITIVE_KEY_PATTERN = /^(authorization|proxy-authorization|cookie|set-cookie|x-api-key|access[_-]?token|refresh[_-]?token|password)$/i

// Catches a bearer token wherever it ends up embedded in a free-form string (e.g. a
// message built with `JSON.stringify(error)` upstream) that the key-based pass can't see.
const BEARER_TOKEN_PATTERN = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi

// Recursively deep-clones a value, replacing sensitive keys and bearer-token-shaped
// strings with a redaction marker. Cycle-safe so serializing an axios error (whose
// config/request references can be circular) can't throw or loop.
function redactSensitiveData(value: any, seen = new WeakSet()): any {
  if (typeof value === 'string') {
    return value.replace(BEARER_TOKEN_PATTERN, `Bearer ${REDACTED}`)
  }

  if (value === null || typeof value !== 'object') {
    return value
  }

  if (seen.has(value)) {
    return '[Circular]'
  }
  seen.add(value)

  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveData(item, seen))
  }

  const result: { [key: string]: any } = {}
  for (const key of Object.keys(value)) {
    result[key] = SENSITIVE_KEY_PATTERN.test(key) ? REDACTED : redactSensitiveData(value[key], seen)
  }
  return result
}

// Serializes an arbitrary value (typically an Error) with sensitive data stripped.
// Prefers the value's own serialization first: AxiosError.toJSON() yields a curated,
// de-circularized { message, name, stack, config, code, status } that deliberately omits
// the bulky request/response objects (response bodies can themselves carry tokens, e.g.
// a token-refresh payload). We then redact the resulting plain structure.
// If that serialization throws — a circular object with no toJSON — fall back to redacting
// the live object directly; redactSensitiveData is itself cycle-safe, so the subsequent
// stringify of its de-circularized output won't throw.
function safeStringify(value: any): string {
  try {
    const serialized = JSON.parse(JSON.stringify(value))
    return JSON.stringify(redactSensitiveData(serialized))
  } catch {
    try {
      return JSON.stringify(redactSensitiveData(value))
    } catch {
      return ''
    }
  }
}

interface CreateErrorLogEntryParameter {
  Browser?: string
  ClientIP?: string
  ClientOS?: string
  Culture?: string
  Environment?: string
  InnerExceptions?: string
  MiscInfo?: string
  RequestInfo?: string
  RequestUrl?: string
  SourceApplication?: string
  StackTrace?: string
  TimeZone?: string
  Title?: string
  Type?: string
  UrlReferrer?: string
  Username?: string
  Message?: string
  UserGuid?: string
  OrganizationId?: number
  AdditionalProperties?: { [key: string]: any }
}

export async function logErrorToApi(
  title: string,
  message: string,
  error?: any,
  url?: string,
  additionalProperties?: { [key: string]: any },
): Promise<void> {
  try {
    const errorLogEntry: CreateErrorLogEntryParameter = {
      Title: title,
      Message: redactSensitiveData(message),
      SourceApplication: 'OWA',
      Environment: configuration.NODE_ENV,
      Username: state.userEmail,
      UserGuid: state.getCirrusUserGuid(),
      Type: error?.name || 'Error',
      StackTrace: redactSensitiveData(error?.stack || ''),
      InnerExceptions: redactSensitiveData(error?.message || ''),
      MiscInfo: safeStringify(error),
      RequestUrl: url || window.location.href,
      UrlReferrer: document.referrer,
      Browser: navigator.userAgent,
      ClientOS: navigator.platform,
      Culture: navigator.language,
      TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      AdditionalProperties: redactSensitiveData({
        ...additionalProperties,
        errorType: error?.constructor?.name,
        errorCode: error?.code,
        errorStatus: error?.status,
        timestamp: new Date().toISOString(),
      }),
    }

    await fetch(configuration.LOGGING_API_BASEURL + '/api/logs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(errorLogEntry),
    })
  } catch (loggingError) {
    // Fallback to console if the logging API fails
    console.error('Failed to log error to API:', loggingError)
    console.error('Original error:', error)
  }
}
